// ============================================================================
// CostCenterTrend.jsx
// Month by month, cost centre by cost centre, over whatever range the reader
// picks — three months or every month loaded. Shared by both tabs:
//
//   kind="headcount"    heads, whole numbers          /hr-headcount/trend/cost-centers
//   kind="allocations"  allocated heads, seated heads  /hr-headcount/allocations/trend
//                       and the gap between them
//   kind="positions"    heads by position within each  /hr-headcount/positions/trend
//                       cost center; budget is not in the portal yet
//
// Two views of one response. The table holds every cost centre and every
// month, because that is the thing people export to a spreadsheet to build by
// hand. The chart holds at most four lines: past four, colours stop being
// tellable apart (chart.coVisible is the all-pairs-validated set and has four
// slots), and a line chart of sixty cost centres is a texture, not a chart.
//
// The range is sent to the server rather than sliced here, so a reader who
// wants three years is not made to download them to look at three months, and
// one who wants all of it is not capped.
// ============================================================================

import { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { api } from '../../services/api';
import { c, t, r, card, tbl, chart } from '../../theme';
import LoadingSpinner from '../Shared/LoadingSpinner';
import MultiSelect from '../ContactCenter/MultiSelect';

const MAX_LINES = chart.coVisible.length;
const PRESETS = [3, 6, 12, 24];

const monthLabel = (iso) =>
  new Date(`${String(iso).slice(0, 10)}T00:00:00`).toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
// '2026-07-01' -> '2026-07', the form the API takes.
const ym = (iso) => String(iso).slice(0, 7);
// n months before a 'YYYY-MM', as 'YYYY-MM'.
const shiftMonth = (month, n) => {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y, m - 1 - n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const thisMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const KINDS = {
  headcount: {
    url: '/hr-headcount/trend/cost-centers',
    title: 'Headcount by cost center, by month',
    metrics: [{ key: 'heads', label: 'Heads', decimals: 0 }],
  },
  positions: {
    url: '/hr-headcount/positions/trend',
    title: 'Positions by month',
    metrics: [{ key: 'heads', label: 'Current', decimals: 0 }],
    rowNoun: 'Position',
  },
  allocations: {
    url: '/hr-headcount/allocations/trend',
    title: 'Allocations by cost center, by month',
    metrics: [
      { key: 'allocated', label: 'Allocated', decimals: 2 },
      { key: 'heads',     label: 'Heads',     decimals: 0 },
      { key: 'gap',       label: 'Cost minus heads', decimals: 2, signed: true },
    ],
  },
};

const fmt = (v, decimals, signed) => {
  const n = Number(v ?? 0);
  const s = n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  return signed && n > 0 ? `+${s}` : s;
};

const s = {
  head:    { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 12 },
  controls:{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  label:   { ...t.label, color: c.textMuted, display: 'flex', alignItems: 'center', gap: 6 },
  input:   { ...t.bodySm, padding: '5px 8px', border: `1px solid ${c.borderStrong}`, borderRadius: 6,
             fontFamily: 'inherit', color: c.textStrong, background: c.surface },
  stickyTh: { position: 'sticky', left: 0, zIndex: 2, background: c.surfaceSunk, minWidth: 190 },
  stickyTd: { position: 'sticky', left: 0, zIndex: 1, background: c.surface, minWidth: 190 },
};

// A position row reads "Driver — 200 Salt Lake" wherever a single label is
// needed (legend, picker); a cost-center row is just its name.
const labelOf = (row) => (row.position ? `${row.position} — ${row.raw_cost_center}` : row.raw_cost_center);

// `costCenter` (positions only) is the tab's own Cost Center picker, applied
// here too so the trend and the month table below it never disagree on scope.
export default function CostCenterTrend({ kind = 'headcount', onSelectCostCenter, costCenter = '' }) {
  const cfg = KINDS[kind];
  // Default: the last twelve months up to now. Once the server has said which
  // months exist, the presets count back from the newest of them instead, so
  // a feed that is a month behind does not quietly show eleven.
  const [range, setRange] = useState({ from: shiftMonth(thisMonth(), 11), to: '', preset: 12 });
  const [metric, setMetric] = useState(cfg.metrics[0].key);
  const [picked, setPicked] = useState([]);

  const params = {};
  if (range.from) params.from = range.from;
  if (range.to) params.to = range.to;

  const q = useQuery({
    queryKey: ['hr-cc-trend', kind, range.from, range.to],
    queryFn: () => api.get(cfg.url, { params }),
    // The range controls live in this data; without this they unmount while
    // the next range loads and the reader loses their place.
    placeholderData: keepPreviousData,
  });

  const available = (q.data?.available_months || []).map(ym);
  const latest = available[available.length - 1] || thisMonth();
  const months = q.data?.months || [];
  const m = cfg.metrics.find((x) => x.key === metric) || cfg.metrics[0];

  const rows = useMemo(() => (q.data?.cost_centers || [])
    .filter((cc) => !costCenter || cc.raw_cost_center === costCenter)
    .map((cc) => ({
      ...cc,
      gap: cc.allocated ? cc.allocated.map((a, i) => Math.round((a - (cc.heads?.[i] || 0)) * 100) / 100) : undefined,
    })), [q.data, costCenter]);

  // The four lines: the reader's picks if any, else the four largest in the
  // newest month — the cost centres a total is mostly made of.
  const keyOf = (cc) => cc.key;
  const byKey = new Map(rows.map((cc) => [keyOf(cc), cc]));
  const lastIdx = months.length - 1;
  const defaultLines = [...rows]
    .sort((a, b) => Math.abs(b[m.key]?.[lastIdx] || 0) - Math.abs(a[m.key]?.[lastIdx] || 0))
    .slice(0, MAX_LINES).map(keyOf);
  const pickedPresent = picked.filter((k) => byKey.has(k));
  const lineKeys = (pickedPresent.length ? pickedPresent : defaultLines).slice(0, MAX_LINES);

  const chartData = months.map((mo, i) => {
    const point = { month: monthLabel(mo.month) };
    for (const k of lineKeys) point[k] = byKey.get(k)?.[m.key]?.[i] ?? 0;
    return point;
  });

  const choosePreset = (n) => setRange(n === 'all'
    ? { from: '', to: '', preset: 'all' }
    : { from: shiftMonth(latest, n - 1), to: '', preset: n });

  const totals = months.map((_, i) => rows.reduce((a, cc) => a + Number(cc[m.key]?.[i] || 0), 0));
  const marks = { rebuilt: '*', carried: '†' };

  return (
    <div style={{ ...card.base, padding: '18px 20px', marginBottom: 16 }}>
      <div style={s.head}>
        <div>
          <div style={{ ...t.label, color: c.textMuted }}>{cfg.title}</div>
          <div style={{ ...t.bodySm, color: c.textFaint }}>
            One column per month — the last snapshot committed for it
            {months.length ? ` · ${months.length} month${months.length === 1 ? '' : 's'} shown of ${available.length} loaded` : ''}
          </div>
        </div>
        <div style={s.controls}>
          {PRESETS.map((n) => (
            <Pill key={n} active={range.preset === n} onClick={() => choosePreset(n)}>{n} mo</Pill>
          ))}
          <Pill active={range.preset === 'all'} onClick={() => choosePreset('all')}>All</Pill>
          <label style={s.label}>
            From
            <select id={`${kind}-trend-from`} aria-label="From month" style={s.input} value={range.from}
                    onChange={(e) => setRange((p) => ({ ...p, from: e.target.value, preset: null }))}>
              <option value="">Earliest</option>
              {available.map((mo) => (
                <option key={mo} value={mo} disabled={!!range.to && mo > range.to}>{monthLabel(`${mo}-01`)}</option>
              ))}
            </select>
          </label>
          <label style={s.label}>
            To
            <select id={`${kind}-trend-to`} aria-label="To month" style={s.input} value={range.to}
                    onChange={(e) => setRange((p) => ({ ...p, to: e.target.value, preset: null }))}>
              <option value="">Latest</option>
              {available.map((mo) => (
                <option key={mo} value={mo} disabled={!!range.from && mo < range.from}>{monthLabel(`${mo}-01`)}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {q.isLoading && <LoadingSpinner />}
      {q.error && <div style={{ ...t.bodySm, color: c.dangerText }}>{q.error.message || 'Could not load the trend.'}</div>}

      {q.data && !months.length && (
        <div style={{ ...t.bodySm, color: c.textMuted }}>
          {available.length
            ? 'No month in that range has a snapshot. Widen the range, or pick All.'
            : kind === 'allocations'
              ? 'No allocation file has been loaded yet, so there is nothing to trend.'
              : 'No snapshot has been loaded yet.'}
        </div>
      )}

      {months.length > 0 && (
        <>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
            {cfg.metrics.length > 1 && cfg.metrics.map((x) => (
              <Pill key={x.key} active={metric === x.key} onClick={() => setMetric(x.key)}>{x.label}</Pill>
            ))}
            {rows.length > 1 && (
              <MultiSelect
                label="Chart" options={rows.map(keyOf)} selected={pickedPresent} onChange={setPicked}
                allLabel="Largest four" noun="cost centers" emptyMeansAll align="left"
                displayFn={(k) => (byKey.has(k) ? labelOf(byKey.get(k)) : k)}
              />
            )}
            <span style={{ ...t.micro, color: c.textFaint }}>
              {pickedPresent.length > MAX_LINES
                ? `Charting the first ${MAX_LINES} of ${pickedPresent.length} chosen — four lines is as many as stay tellable apart. The table shows every cost center.`
                : pickedPresent.length
                  ? 'The table below shows every cost center.'
                  : `Charting the ${Math.min(MAX_LINES, rows.length)} largest in the newest month. Pick others with Chart.`}
            </span>
          </div>

          {months.length === 1 ? (
            <div style={{ ...t.bodySm, color: c.textMuted, margin: '6px 0 12px' }}>
              One month in this range, so there is no line to draw — the table has its figures.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={chartData} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid stroke={chart.grid} vertical={false} />
                <XAxis dataKey="month" tick={{ fill: chart.axisText, fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fill: chart.axisText, fontSize: 11 }} tickLine={false} axisLine={false} width={48} />
                <Tooltip
                  contentStyle={{ border: `1px solid ${c.border}`, borderRadius: r.md, fontSize: 12 }}
                  formatter={(v, k) => [fmt(v, m.decimals, m.signed), byKey.has(k) ? labelOf(byKey.get(k)) : k]}
                />
                <Legend formatter={(k) => (byKey.has(k) ? labelOf(byKey.get(k)) : k)} wrapperStyle={{ fontSize: 12 }} />
                {lineKeys.map((k, i) => (
                  <Line key={k} type="monotone" dataKey={k} stroke={chart.coVisible[i]} strokeWidth={2}
                        dot={{ r: 2.5 }} isAnimationActive={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}

          <div style={{ overflow: 'auto', maxHeight: 520, marginTop: 12, border: `1px solid ${c.border}`, borderRadius: r.md }}>
            <table style={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: '100%' }}>
              <thead>
                <tr>
                  <th style={{ ...tbl.thSm, ...s.stickyTh, top: 0, zIndex: 3 }}>{cfg.rowNoun || 'Cost Center'}</th>
                  {months.map((mo) => (
                    <th key={mo.month} style={{ ...tbl.thRSm, position: 'sticky', top: 0, zIndex: 1,
                                               background: c.surfaceSunk, whiteSpace: 'nowrap' }}
                        title={mo.basis && mo.basis !== 'own'
                          ? `Read from the ${String(mo.allocation_snapshot_date).slice(0, 10)} allocation file`
                          : `Snapshot ${String(mo.snapshot_date).slice(0, 10)}`}>
                      {monthLabel(mo.month)}{marks[mo.basis] || ''}{mo.is_provisional ? ' (prov.)' : ''}
                    </th>
                  ))}
                  <th style={{ ...tbl.thRSm, position: 'sticky', top: 0, zIndex: 1, background: c.surfaceSunk, whiteSpace: 'nowrap' }}>
                    Change
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((cc) => {
                  const vals = cc[m.key] || [];
                  const change = months.length > 1 ? Number(vals[lastIdx] || 0) - Number(vals[0] || 0) : null;
                  const clickable = onSelectCostCenter && cc.cost_center_id;
                  return (
                    <tr key={cc.key} style={clickable ? tbl.trHover : tbl.tr}
                        onClick={() => clickable && onSelectCostCenter(cc.cost_center_id)}>
                      <td style={{ ...tbl.tdSm, ...s.stickyTd, whiteSpace: 'nowrap' }}>
                        {cc.position ?? cc.raw_cost_center}
                        {cc.position != null && (
                          <div style={{ ...t.micro, color: c.textFaint }}>{cc.raw_cost_center}</div>
                        )}
                      </td>
                      {vals.map((v, i) => (
                        <td key={months[i].month} style={{ ...tbl.tdRSm, color: Number(v) === 0 ? c.textFaint : c.text }}>
                          {fmt(v, m.decimals, m.signed)}
                        </td>
                      ))}
                      <td style={{ ...tbl.tdRSm, fontWeight: 600,
                                   color: !change ? c.textMuted : change > 0 ? c.successText : c.dangerText }}>
                        {change === null ? '—' : fmt(change, m.decimals, true)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 600 }}>
                  <td style={{ ...tbl.tdSm, ...s.stickyTd, background: c.surfaceSunk }}>Total</td>
                  {totals.map((v, i) => (
                    <td key={months[i].month} style={{ ...tbl.tdRSm, background: c.surfaceSunk }}>{fmt(v, m.decimals, m.signed)}</td>
                  ))}
                  <td style={{ ...tbl.tdRSm, background: c.surfaceSunk }}>
                    {months.length > 1 ? fmt(totals[lastIdx] - totals[0], m.decimals, true) : '—'}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {months.some((mo) => mo.basis === 'rebuilt' || mo.basis === 'carried') && (
            <div style={{ ...t.micro, color: c.textMuted, marginTop: 6 }}>
              {months.some((mo) => mo.basis === 'rebuilt') && (
                <>* No allocation file for that month — rebuilt from the dated allocation lines in the next file loaded after it, for the people on that month’s roster. </>
              )}
              {months.some((mo) => mo.basis === 'carried') && (
                <>† No allocation file for that month or after — the newest earlier file is carried forward.</>
              )}
            </div>
          )}
          {kind === 'positions' && (
            <div style={{ ...t.micro, color: c.textMuted, marginTop: 6 }}>
              Current employees only. Budget figures are not in the portal yet, so there is no budget line to trend against.
            </div>
          )}
          {months.some((mo) => mo.is_provisional) && (
            <div style={{ ...t.micro, color: c.warningText, marginTop: 4 }}>
              Months marked (prov.) were known to be incomplete extracts.
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Pill({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick} style={{
      ...t.micro, padding: '4px 10px', borderRadius: r.pill, cursor: 'pointer',
      border: `1px solid ${active ? c.navy : c.border}`,
      background: active ? c.navy : c.surface,
      color: active ? c.onNavy : c.textMuted,
    }}>{children}</button>
  );
}
